import { fabric } from 'fabric';

/**
 * Fabric extensions the Design editor relies on, applied once per page.
 *
 * Imported for its side effects by the editor AND by the headless renderer
 * (canvas/design-render.ts): a scene saved by one must draw identically in the
 * other, so anything the editor teaches Fabric to draw lives here rather than in
 * a component.
 *
 * Everything stays a standard Fabric type — no custom classes — so a scene
 * still enlivens anywhere fabric 5 runs. The extras are plain properties that
 * ride along in toObject().
 */

/**
 * Extra properties serialised with every object. Fabric only writes the props
 * it knows; these carry the editor's meaning (what a layer is for, its name,
 * whether it is locked, which mask it wears) across save → reopen.
 */
export const CS_EXTRA_PROPS = [
    'csId',
    'csName',
    'csNameUser',
    'csGfxId',
    'csGfxWrap',
    'csGfxFrom',
    'csGfxTo',
    'csGfxPadX',
    'csGfxPadY',
    'csGfxUpper',
    'csRole',
    'csLocked',
    'csMask',
    'csMaskRadius',
    'csColorRole',
    'csFontRole',
    'csShape',
    'csSides',
    'csInner',
    'csIcon',
    'csColorSet',
    'bgPadX',
    'bgPadY',
    'bgRadius',
    'bgStroke',
    'bgStrokeWidth',
    // Agent hint consumed by the headless renderer; kept so a scene that still
    // carries it round-trips intact.
    'fitLogo',
    // Locking survives a reload only if these do.
    'selectable',
    'evented',
    'lockMovementX',
    'lockMovementY',
    'lockScalingX',
    'lockScalingY',
    'lockRotation',
    'hasControls',
];

let installed = false;

export function installFabricExtensions(): void {
    if (installed) return;
    installed = true;

    // Fabric rounds every serialised number to 2 decimals. A picture scaled
    // 1080/1024 = 1.0547 came back as 1.05 — 5px short of the artboard, a
    // white strip down the edge of every reloaded or headlessly rendered design.
    (fabric as any).Object.NUM_FRACTION_DIGITS = 6;

    const ObjectProto: any = (fabric as any).Object.prototype;
    const origToObject = ObjectProto.toObject;
    ObjectProto.toObject = function toObjectWithExtras(propertiesToInclude?: string[]) {
        const out = origToObject.call(this, [...(propertiesToInclude || []), ...CS_EXTRA_PROPS]);
        // Drop the extras the layer does not use so plain scenes stay plain.
        for (const k of CS_EXTRA_PROPS) {
            if (out[k] === undefined || out[k] === null) delete out[k];
        }
        // Defaults are noise in the JSON and in the snapshots undo compares.
        if (out.selectable === true) delete out.selectable;
        if (out.evented === true) delete out.evented;
        if (out.hasControls === true) delete out.hasControls;
        for (const k of ['lockMovementX', 'lockMovementY', 'lockScalingX', 'lockScalingY', 'lockRotation']) {
            if (out[k] === false) delete out[k];
        }
        return out;
    };

    // ── Text background as a padded, rounded plate ────────────────────
    //
    // Fabric's backgroundColor fills the text's exact box: no padding, square
    // corners — fine for a highlight, useless for a CTA pill or a price tag.
    // bgPadX / bgPadY / bgRadius grow it into a plate. The text stays a plain,
    // editable, self-measuring Textbox; only how its background is painted
    // changes, so the headless renderer draws it the same way.
    const TextProto: any = (fabric as any).Text.prototype;
    const origRenderBackground = TextProto._renderBackground;
    TextProto._renderBackground = function renderPlate(ctx: CanvasRenderingContext2D) {
        const padX = Number(this.bgPadX) || 0;
        const padY = Number(this.bgPadY) || 0;
        const radius = Number(this.bgRadius) || 0;
        if (!this.backgroundColor || (!padX && !padY && !radius)) {
            origRenderBackground.call(this, ctx);
            return;
        }
        const w = (this.width || 0) + padX * 2;
        const h = (this.height || 0) + padY * 2;
        const x = -(this.width || 0) / 2 - padX;
        const y = -(this.height || 0) / 2 - padY;
        const r = Math.max(0, Math.min(radius, w / 2, h / 2));
        ctx.fillStyle = this.backgroundColor;
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.arcTo(x + w, y, x + w, y + r, r);
        ctx.lineTo(x + w, y + h - r);
        ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
        ctx.lineTo(x + r, y + h);
        ctx.arcTo(x, y + h, x, y + h - r, r);
        ctx.lineTo(x, y + r);
        ctx.arcTo(x, y, x + r, y, r);
        ctx.closePath();
        ctx.fill();
        this._removeShadow(ctx);
        // An optional hairline around the plate — spec chips on a light ground.
        const sw = Number(this.bgStrokeWidth) || 0;
        if (sw > 0 && this.bgStroke) {
            ctx.lineWidth = sw;
            ctx.strokeStyle = this.bgStroke;
            ctx.stroke();
        }
    };

    // A plate drawn outside the text's box would be cropped by the object cache,
    // which is sized to the box. Plated text draws direct; it is a handful of
    // layers per design, not thousands.
    const origShouldCache = ObjectProto.shouldCache;
    ObjectProto.shouldCache = function shouldCacheUnlessPlated() {
        if (this.backgroundColor && (this.bgPadX || this.bgPadY) && typeof this.text === 'string') {
            this.ownCaching = false;
            return false;
        }
        return origShouldCache.call(this);
    };
}

installFabricExtensions();

/** A short random id for layers (stable React keys + layer panel identity). */
export function newLayerId(): string {
    return Math.random().toString(36).slice(2, 10);
}

/** First family in a CSS font stack, unquoted: `"Playfair Display", Inter` → `Playfair Display`. */
export function primaryFamily(fontFamily?: string): string {
    if (!fontFamily) return 'Inter';
    const first = fontFamily.split(',')[0].trim();
    return first.replace(/^["']|["']$/g, '') || 'Inter';
}

export const isTextObject = (o?: fabric.Object | null): o is fabric.Textbox =>
    !!o && (o.type === 'textbox' || o.type === 'i-text' || o.type === 'text');

export const isImageObject = (o?: fabric.Object | null): o is fabric.Image => !!o && o.type === 'image';
